import * as echarts from "echarts";
import type { ChartConfig } from "@bi-studio/shared/lib";
import type { ChartData, ChartSize } from "./types";
import {
    getChartInstance,
    setupResize,
    clearContainer,
    resolveColors,
} from "./utils";

export const renderGaugeChart = (
    container: HTMLElement | null,
    data: ChartData,
    config: ChartConfig,
    _size?: ChartSize,
    theme: 'light' | 'dark' = 'light'
): HTMLElement | null => {
    if (!container || typeof window === "undefined") return null;

    clearContainer(container);

    // Set container size
    container.style.width = "100%";
    container.style.height = "100%";

    const chartInstance = getChartInstance(container, theme);
    const colors = resolveColors(config, theme);

    // Map data to a single value
    const valueField = config.valueField || "value";
    let value = 0;

    if (data.length > 0) {
        // Use the first row for the gauge value
        value = Number(data[0][valueField]) || 0;
    }

    const minVal = config.minValue !== undefined ? Number(config.minValue) : 0;
    const maxVal = config.maxValue !== undefined ? Number(config.maxValue) : 100;
    const unit = config.unit || "";
    const title = config.title || "Score";

    const option: echarts.EChartsOption = {
        backgroundColor: 'transparent',
        tooltip: {
            formatter: `{a} <br/>{b} : {c}${unit}`
        },
        series: [
            {
                name: title,
                type: 'gauge',
                min: minVal,
                max: maxVal,
                progress: {
                    show: true,
                    width: 18,
                    itemStyle: {
                        color: colors[0]
                    }
                },
                axisLine: {
                    lineStyle: {
                        width: 18,
                        color: theme === 'dark' ? [[1, '#333']] : [[1, '#eee']]
                    }
                },
                axisTick: {
                    show: false
                },
                splitLine: {
                    distance: -18,
                    length: 18,
                    lineStyle: {
                        color: theme === 'dark' ? '#111' : '#fff',
                        width: 2
                    }
                },
                axisLabel: {
                    distance: 25,
                    color: theme === 'dark' ? '#999' : '#999',
                    fontSize: 12
                },
                anchor: {
                    show: true,
                    showAbove: true,
                    size: 25,
                    itemStyle: {
                        borderWidth: 10,
                        borderColor: colors[0]
                    }
                },
                pointer: {
                    itemStyle: {
                        color: colors[0]
                    }
                },
                title: {
                    show: false
                },
                detail: {
                    valueAnimation: true,
                    fontSize: 40,
                    offsetCenter: [0, '70%'],
                    formatter: `{value}${unit}`,
                    color: theme === 'dark' ? '#fff' : '#333'
                },
                data: [
                    {
                        value: value,
                        name: title
                    }
                ]
            }
        ]
    };

    chartInstance.setOption(option);
    setupResize(container, chartInstance);

    return container;
};
